import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { connectorsApi, connectorKeys } from '@/features/connectors/api';
import type { NewDatabaseConnector } from '@/features/connectors/types';

export function useConnectors() {
  return useQuery({ queryKey: connectorKeys.all, queryFn: connectorsApi.list });
}

export function useConnector(id: string) {
  return useQuery({
    queryKey: connectorKeys.detail(id),
    queryFn: () => connectorsApi.get(id),
    enabled: id.length > 0,
  });
}

export function useConnectorDatasets(connectorId: string) {
  return useQuery({
    queryKey: connectorKeys.datasets(connectorId),
    queryFn: () => connectorsApi.datasets(connectorId),
    enabled: connectorId.length > 0,
  });
}

export function useDataset(id: string) {
  return useQuery({
    queryKey: connectorKeys.dataset(id),
    queryFn: () => connectorsApi.dataset(id),
    enabled: id.length > 0,
  });
}

export function useDatasetPreview(id: string, enabled: boolean) {
  return useQuery({
    queryKey: connectorKeys.preview(id),
    queryFn: () => connectorsApi.preview(id),
    enabled: enabled && id.length > 0,
  });
}

function useInvalidateConnectors() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: connectorKeys.all });
}

export function useCreateDatabaseConnector() {
  const invalidate = useInvalidateConnectors();
  return useMutation({
    mutationFn: (input: NewDatabaseConnector) => connectorsApi.createDatabase(input),
    onSuccess: invalidate,
  });
}

export function useUploadFileConnector() {
  const invalidate = useInvalidateConnectors();
  return useMutation({
    mutationFn: ({ name, file, sheet }: { name: string; file: File; sheet?: string }) =>
      connectorsApi.uploadFile(name, file, sheet),
    onSuccess: invalidate,
  });
}

export function useTestConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => connectorsApi.test(id),
    onSuccess: (connector) => {
      queryClient.invalidateQueries({ queryKey: connectorKeys.detail(connector.id) });
    },
  });
}

export function useSyncConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => connectorsApi.sync(id),
    onSuccess: (connector) => {
      queryClient.invalidateQueries({ queryKey: connectorKeys.detail(connector.id) });
      queryClient.invalidateQueries({ queryKey: connectorKeys.datasets(connector.id) });
    },
  });
}

export function useDeleteConnector() {
  const invalidate = useInvalidateConnectors();
  return useMutation({
    mutationFn: (id: string) => connectorsApi.remove(id),
    onSuccess: invalidate,
  });
}
